/** Web Socket Library. */
import { io } from 'socket.io-client';

/** Redux Slices.. */
import {
    connectionEstablished,
    connectionLost,
    connectionRefused,
    syncSettled,
} from './shared/store/slices/socketStatusSlice';

/** Internal Components. */
import { clearFetchedHistoryCache } from './features/shipments/hooks/useFetchHistory';
import { onReadsIdle, pendingReadCount, resetPendingReads } from './shared/lib/readTracker';

const auth0API = process.env.REACT_APP_AUTH0_AUTHORIZATION_API;

/**
 * A blip of a few seconds is healed within a second; a long outage keeps being
 * retried at a pace (up to half a minute apart) the server can absorb from every
 * open tab at once.
 */
export const RECONNECT_DELAY_MS = 1000;
export const RECONNECT_DELAY_MAX_MS = 30000;
/** How long the badge gives the open screens to start their re-reads before it checks whether any are waiting. */
export const SYNC_SETTLE_MS = 1000;
/** The badge never stays on "syncing" longer than this, even if a screen's re-read is never answered. */
export const SYNC_TIMEOUT_MS = 20000;
/**
 * A server that turns the handshake away this many times in a row is saying the
 * login itself is no longer good; more attempts would only repeat the refusal, so
 * the operator is asked to sign in again instead.
 */
export const SERVER_REFUSAL_LIMIT = 5;
/**
 * Reason the server attaches when it turns a handshake away because the login
 * itself is not acceptable (missing, expired or forged token). Other reasons —
 * the server could not create a session or hit an error — clear up on their own
 * and are retried like a network failure.
 */
export const AUTH_REFUSAL_REASON = 'unauthorized';
/**
 * Reason the server attaches when the account itself has been deactivated
 * (Inactive role). Retrying can never succeed, so the page goes straight to
 * the inactive notice instead of reconnecting.
 */
export const INACTIVE_REFUSAL_REASON = 'inactive';

let socket;
let reconnectAttempts = 0;
let reconnectTimer = null;
let syncSettleTimer = null;
let syncTimeoutTimer = null;
let stopWaitingForReads = null;
let hasConnectedBefore = false;
let serverRefusals = 0;
let refusedForGood = false;

/** How long the operator waits before the next attempt: 1s, 2s, 4s, ... never more than 30s. */
export const reconnectDelay = (attempt) => Math.min(RECONNECT_DELAY_MS * 2 ** attempt, RECONNECT_DELAY_MAX_MS);

const clearReconnectTimer = () => {
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
};

const clearSyncTimers = () => {
    if (syncSettleTimer) clearTimeout(syncSettleTimer);
    if (syncTimeoutTimer) clearTimeout(syncTimeoutTimer);
    if (stopWaitingForReads) stopWaitingForReads();
    syncSettleTimer = null;
    syncTimeoutTimer = null;
    stopWaitingForReads = null;
};

/**
 * After a reconnection the badge says "syncing" until the open screens have re-read
 * their data. Screens start those reads in their effects right after the connection
 * state changes, so we wait a short grace period, then hold "syncing" until no read
 * is left waiting — or until the cap, so an unanswered read cannot pin the badge.
 */
const finishSyncWhenScreensCaughtUp = (dispatch) => {
    const settle = () => {
        clearSyncTimers();
        dispatch(syncSettled());
    };
    syncSettleTimer = setTimeout(() => {
        syncSettleTimer = null;
        if (pendingReadCount() === 0) {
            settle();
            return;
        }
        stopWaitingForReads = onReadsIdle(settle);
    }, SYNC_SETTLE_MS);
    syncTimeoutTimer = setTimeout(settle, SYNC_TIMEOUT_MS);
};

/**
 * Keep trying to get back to the server for as long as the tab is open. A network
 * blip, a closed laptop lid or a server deploy must never leave the tab dead until
 * the operator refreshes: every failed attempt books the next one, a little later
 * each time so an outage does not turn into a retry storm.
 */
const scheduleReconnect = () => {
    if (!socket || socket.connected || reconnectTimer || refusedForGood) return;
    const delay = reconnectDelay(reconnectAttempts);
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        socket.connect();
    }, delay);
};

/**
 * The operator's machine just told us the link is probably back (network came up,
 * tab became visible): skip the remaining wait and try right now, starting the
 * backoff over from one second.
 */
export const reconnectNow = () => {
    if (!socket || socket.connected || refusedForGood) return;
    clearReconnectTimer();
    reconnectAttempts = 0;
    socket.connect();
};

/** Whether a save sent right now would actually reach the server. */
export const isSocketConnected = () => Boolean(socket?.connected);

/**
 * Opens the operator's live link to the server for the whole page session: teammates'
 * edits arrive through it and every save goes out over it. Once opened it is never
 * given up — an outage of any length ends in the same link coming back on its own,
 * with a token that is still valid, so the operator never has to refresh the page.
 */
export const initSocket = async function (getAccessTokenSilently, email, dispatch) {
    if (socket) return socket;

    const URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3002';

    const fetchToken = () =>
        getAccessTokenSilently({
            authorizationParams: {
                audience: auth0API,
            },
        });

    // A page opened while Auth0 is briefly unreachable must still end up connected:
    // the link is built regardless and every attempt asks for a token again.
    let lastToken = null;
    try {
        lastToken = await fetchToken();
    } catch (e) {
        console.error('Could not fetch the access token for the socket handshake:', e);
    }

    /**
     * Hand the server a token that is valid *at this attempt*. A token frozen at
     * page load expires long before a multi-hour session ends, so a reconnection
     * after that would be refused forever. The query string keeps the latest token
     * too, for a server that still reads the handshake from the URL.
     */
    const provideAuth = (cb) => {
        fetchToken()
            .then((token) => {
                lastToken = token;
                if (socket?.io?.opts?.query) socket.io.opts.query.token = token;
                cb({ token, email });
            })
            .catch((e) => {
                console.error('Token refresh failed; reconnecting with the previous token:', e);
                cb({ token: lastToken, email });
            });
    };

    socket = io(URL, {
        transports: ['websocket'],
        query: {
            token: lastToken,
            email: email,
        },
        auth: provideAuth,
        autoConnect: true,

        // socket.io's own retry gives up when the server refuses a handshake and
        // cannot be hurried when the network comes back; either would leave the
        // operator staring at a dead tab, so retries are scheduled here instead.
        reconnection: false,
        timeout: 5000,
    });

    socket.on('connect', function () {
        const isReconnect = hasConnectedBefore;
        hasConnectedBefore = true;
        reconnectAttempts = 0;
        serverRefusals = 0;
        clearReconnectTimer();
        clearSyncTimers();
        console.log(isReconnect ? 'Socket reconnected' : 'Socket connected');

        // Edits broadcast while disconnected never arrive retroactively; dropping
        // the history cache makes each panel re-read its full history on next
        // mount so the gap heals. On the initial connect the cache is empty.
        clearFetchedHistoryCache();

        dispatch(connectionEstablished({ isReconnect }));

        if (isReconnect) finishSyncWhenScreensCaughtUp(dispatch);
    });

    socket.on('connect_error', function (error) {
        // An account the server reports as deactivated will never be let in again;
        // stop reconnecting and show the operator the inactive notice.
        if (socket.active === false && error?.message === INACTIVE_REFUSAL_REASON) {
            console.warn('Server refused the connection: account is inactive');
            refusedForGood = true;
            clearReconnectTimer();
            dispatch(connectionRefused());
            if (typeof window !== 'undefined' && window.location.pathname !== '/inactive') {
                window.location.assign('/inactive');
            }
            return;
        }
        // A dead network, a server that is down or a server-side error deserves
        // retrying for as long as it takes; only a handshake the server turned away
        // because the login is bad (socket.active === false with the auth reason)
        // counts toward giving up.
        const refusedForBadLogin = socket.active === false && error?.message === AUTH_REFUSAL_REASON;
        serverRefusals = refusedForBadLogin ? serverRefusals + 1 : 0;

        if (refusedForBadLogin && serverRefusals >= SERVER_REFUSAL_LIMIT) {
            console.error('Server refused the connection repeatedly; stopped retrying:', error.message);
            refusedForGood = true;
            clearReconnectTimer();
            dispatch(connectionRefused());
            return;
        }

        console.warn('Socket connection attempt failed:', error.message);
        dispatch(connectionLost());
        scheduleReconnect();
    });

    socket.on('disconnect', function (reason) {
        console.log('Socket disconnected:', reason);
        clearSyncTimers();
        resetPendingReads();
        dispatch(connectionLost());
        // Only a link the app closed on purpose stays closed; every other drop is
        // an outage the operator expects to see healed.
        if (reason !== 'io client disconnect') scheduleReconnect();
    });

    if (typeof window !== 'undefined') {
        window.addEventListener('online', reconnectNow);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') reconnectNow();
        });
    }

    return socket;
};

export const getSocket = () => socket;
